import type { ClaimDetail, ClaimRow } from '../types/pettyCash';
import { formatAmount, round2 } from './pettyCash';

/**
 * Bulk actions on the petty cash claims list (Nov 2026): finance approves many claims at once (the last finance stage
 * posts them to SAP) and records the reimbursement of many posted claims. Each claim still goes through its own
 * request, one after another, so every check of the single action applies; one SAP top-up journal per claim.
 */

/** Why a selected claim is left out of a bulk approval; null when it goes in. */
export function approvalHold(r: ClaimRow): string | null {
  if (!r.canBulkApprove) return 'It is not waiting for your finance decision';
  if (r.lastFinanceStage && r.originalsRequired && r.originalsReceived === false) return 'The original receipts have not been marked as received';
  return null;
}

export interface BulkApprovePlan {
  run: ClaimRow[];
  held: Array<{ row: ClaimRow; reason: string }>;
  /** At the last finance stage: approving them posts them to SAP. */
  posting: number;
  /** At an earlier finance stage: they move to the next one. */
  moving: number;
  total: number;
}

export function bulkApprovePlan(rows: ClaimRow[]): BulkApprovePlan {
  const run: ClaimRow[] = [];
  const held: Array<{ row: ClaimRow; reason: string }> = [];
  for (const row of rows) {
    const reason = approvalHold(row);
    if (reason) held.push({ row, reason });
    else run.push(row);
  }
  return {
    run,
    held,
    posting: run.filter((r) => r.lastFinanceStage).length,
    moving: run.filter((r) => !r.lastFinanceStage).length,
    total: round2(run.reduce((sum, r) => sum + (Number(r.total) || 0), 0)),
  };
}

/** What a reimbursement pays back for a claim: the receipts SAP paid (the claim's total from an older server). */
export const reimburseAmount = (r: ClaimRow): number => round2(r.paidTotal ?? r.total);

export interface HolderGroup {
  key: string;
  holderName: string;
  claims: ClaimRow[];
  total: number;
}

/** Claims to reimburse by holder (each holder is paid by their own transfer or cheque), in the list's order. */
export function groupByHolder(rows: ClaimRow[]): HolderGroup[] {
  const groups = new Map<string, HolderGroup>();
  for (const r of rows) {
    const key = r.holderUserId ? `u${r.holderUserId}` : `n${r.holderName}`;
    const g = groups.get(key) || { key, holderName: r.holderName, claims: [], total: 0 };
    g.claims.push(r);
    g.total = round2(g.total + reimburseAmount(r));
    groups.set(key, g);
  }
  return Array.from(groups.values());
}

/** The holders still without the reference their way of paying needs (a transfer reference, a cheque number). */
export function missingReferences(groups: HolderGroup[], references: Record<string, string>, needsReference: boolean): string[] {
  if (!needsReference) return [];
  return groups.filter((g) => !String(references[g.key] || '').trim()).map((g) => g.holderName);
}

/** How a claim came out of a bulk action. */
export interface Outcome {
  tone: 'ok' | 'warn' | 'error';
  text: string;
}

/** What a bulk approval did to a claim, from the claim the server answered. */
export function approvalOutcome(d: ClaimDetail | undefined): Outcome {
  const status = d?.claim?.status;
  if (status === 'Posting') return { tone: 'ok', text: 'Approved: posting to SAP' };
  if (status === 'Posted') return { tone: 'ok', text: 'Approved and posted to SAP' };
  if (status === 'PartiallyPosted') return { tone: 'warn', text: 'Approved, but SAP refused some receipts: open the claim' };
  if (status === 'FinanceReview') return { tone: 'ok', text: `Approved: now at ${d?.finance?.stageName || 'the next finance stage'}` };
  return { tone: 'ok', text: 'Approved' };
}

/** What a bulk reimbursement did to a claim: recorded, with its SAP top-up journal when the setup posts one. */
export function reimbursementOutcome(d: ClaimDetail | undefined): Outcome {
  const r = d?.reimbursement;
  if (!r) return { tone: 'ok', text: 'Recorded' };
  const paid = `${formatAmount(r.amount)} paid back`;
  if (!r.sap) return { tone: 'ok', text: paid };
  if (r.sap.status === 'Posted') return { tone: 'ok', text: `${paid} · top-up journal ${r.sap.number || r.sap.entry || ''} in SAP` };
  if (r.sap.status === 'Failed') return { tone: 'warn', text: `${paid}, but SAP refused the top-up journal${r.sap.error ? `: ${r.sap.error}` : ''}` };
  return { tone: 'ok', text: `${paid} · the top-up journal is being posted in SAP` };
}

export interface StepResult<T, R> {
  item: T;
  ok: boolean;
  value?: R;
  error?: string;
}

/** The message of a failed request: the server's, else the error's. */
export function errorMessage(e: any, fallback: string): string {
  return String(e?.response?.data?.message || e?.message || fallback);
}

/**
 * Runs `fn` on each item, one after another (a request ends before the next starts). `shouldStop` is asked before
 * each item, so Stop ends the run after the current one; `onProgress` gets the count done and the item starting.
 */
export async function runOneByOne<T, R>(
  items: T[],
  fn: (item: T) => Promise<R>,
  opts: { onProgress?: (done: number, current: T | null) => void; shouldStop?: () => boolean; fallbackError?: string } = {},
): Promise<{ results: Array<StepResult<T, R>>; stopped: boolean }> {
  const results: Array<StepResult<T, R>> = [];
  for (const item of items) {
    if (opts.shouldStop?.()) return { results, stopped: true };
    opts.onProgress?.(results.length, item);
    try {
      results.push({ item, ok: true, value: await fn(item) });
    } catch (e: any) {
      results.push({ item, ok: false, error: errorMessage(e, opts.fallbackError || 'It could not be done') });
    }
  }
  opts.onProgress?.(results.length, null);
  return { results, stopped: false };
}
